import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Spinner } from "@/components/Spinner/Spinner";
import { TablePagination } from "@/components/TablePagination/TablePagination";

import { useGetSignalPriorityHistoryQuery } from "@/pages/Private/redux/signal/signal.api";

import { SignalPriorityHistoryItem } from "../schema/signal";

const formatDate = (dateStr: string): string => {
	const date = new Date(dateStr);

	return date.toLocaleDateString(undefined, {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

const PriorityBadge: FunctionComponent<{ priority: string | null }> = ({ priority }) => {
	const { t } = useTranslation();

	if (!priority) {
		return (
			<span className="text-text-meta italic text-xs">
				{t("list.signals.history.noPreviousPriority")}
			</span>
		);
	}

	const colorMap: Record<string, string> = {
		"Prio 1": "bg-status-success text-white",
		"Prio 2": "bg-status-warning text-white",
		"Prio 3": "bg-gray-400 text-white",
		Disqualified: "bg-status-error text-white",
	};

	return (
		<span
			className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorMap[priority] ?? "bg-gray-200 text-gray-700"}`}
		>
			{priority}
		</span>
	);
};

export const SignalsHistory: FunctionComponent = () => {
	const { t } = useTranslation();
	const [page, setPage] = useState(1);
	const limit = 10;

	const { data, isFetching } = useGetSignalPriorityHistoryQuery({
		page,
		limit,
		sortBy: "createdAt",
		sortOrder: "DESC",
	});

	const historyItems: SignalPriorityHistoryItem[] = data?.data ?? [];
	const meta = data?.meta;

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	if (isFetching) {
		return (
			<div className="flex justify-center py-12">
				<Spinner />
			</div>
		);
	}

	if (historyItems.length === 0) {
		return (
			<div className="text-center py-12 text-text-meta text-sm">
				{t("list.signals.history.noHistory")}
			</div>
		);
	}

	return (
		<div className="w-full">
			<div className="rounded-xl border border-gray-200 bg-brand-white overflow-hidden">
				<table className="w-full text-sm">
					<thead>
						<tr className="border-b border-gray-200 bg-gray-50">
							<th className="px-4 py-3 text-left font-medium text-text-meta">
								{t("list.signals.history.signalTitle")}
							</th>
							<th className="px-4 py-3 text-left font-medium text-text-meta">
								{t("list.signals.history.organizationName")}
							</th>
							<th className="px-4 py-3 text-left font-medium text-text-meta">
								{t("list.signals.history.signalCategory")}
							</th>
							<th className="px-4 py-3 text-left font-medium text-text-meta">
								{t("list.signals.history.user")}
							</th>
							<th className="px-4 py-3 text-left font-medium text-text-meta">
								{t("list.signals.history.previousPriority")}
							</th>
							<th className="px-4 py-3 text-left font-medium text-text-meta">
								{t("list.signals.history.newPriority")}
							</th>
							<th className="px-4 py-3 text-left font-medium text-text-meta">
								{t("list.signals.history.changedAt")}
							</th>
						</tr>
					</thead>
					<tbody>
						{historyItems.map((item) => (
							<tr key={item.id} className="border-b border-gray-200 last:border-b-0">
								<td className="px-4 py-3 text-text">{item.signalTitle ?? "—"}</td>
								<td className="px-4 py-3 text-text">{item.organizationName ?? "—"}</td>
								<td className="px-4 py-3 text-text-meta">{item.signalCategory ?? "—"}</td>
								<td className="px-4 py-3 text-text">
									{item.user.firstName} {item.user.lastName}
								</td>
								<td className="px-4 py-3">
									<PriorityBadge priority={item.previousPriority} />
								</td>
								<td className="px-4 py-3">
									<PriorityBadge priority={item.newPriority} />
								</td>
								<td className="px-4 py-3 text-text-meta">{formatDate(item.createdAt)}</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>

			{meta && meta.pageCount > 1 && (
				<TablePagination
					currentPage={page}
					handleNavigateToPage={handlePageChange}
					limit={limit}
					maxPage={meta.pageCount}
				/>
			)}
		</div>
	);
};
